import React, { useState } from "react";
import { api, formatApiErrorDetail } from "@/lib/api";
import { toast } from "sonner";
import { Send, Lock } from "lucide-react";
import { ago } from "@/lib/mongoMcp";
import ConfirmAction from "@/components/mongo/ConfirmAction";

// Hub: published version + "Publish to all servers". Other servers: what the hub published (read-only).
export default function FieldMapPublishBar({ data, onPublished }) {
    const [note, setNote] = useState("");
    const [busy, setBusy] = useState(false);
    const pub = data.published || {};
    const published = pub.version ? `Published v${pub.version} · ${ago(pub.published_at)}${pub.published_by ? ` by ${pub.published_by}` : ""}` : "Nothing published yet";
    const publish = async () => {
        setBusy(true);
        try {
            const { data: res } = await api.post("/admin/mongo-mcp/field-map/publish", { note: note.trim() || null });
            toast.success(`Field map v${res.version} published to all servers`);
            setNote("");
            onPublished();
        } catch (e) { toast.error(formatApiErrorDetail(e)); }
        finally { setBusy(false); }
    };
    if (data.read_only) {
        return (
            <div className="flex items-center gap-2 px-5 py-2.5 border-b border-white/10 text-[11px] text-white/55" data-testid="field-map-readonly-note">
                <Lock className="w-3 h-3" /> Edited on the Radar hub. {published}{pub.received_at ? ` · received ${ago(pub.received_at)}` : ""}.
            </div>
        );
    }
    const pending = data.pending_changes || 0;
    return (
        <div className={`flex flex-wrap items-center gap-3 px-5 py-2.5 border-b border-white/10 ${pending ? "bg-amber-300/[0.04]" : ""}`} data-testid="field-map-publish-bar">
            <div className="text-[11px] flex-1 min-w-[220px]">
                <span className="text-white/70" data-testid="field-map-published-version">{published}</span>
                <span className={`ml-2 ${pending ? "text-amber-200" : "text-white/40"}`} data-testid="field-map-pending-count">
                    {pending ? `${pending} unpublished change(s) — not enforced anywhere yet` : "every server has this map"}
                </span>
            </div>
            {pending > 0 && (
                <input value={note} onChange={(e) => setNote(e.target.value)} placeholder="What changed (optional)" maxLength={300} data-testid="field-map-publish-note"
                    className="w-56 bg-[#050505] border border-white/10 rounded px-2 py-1.5 text-[11px] focus:outline-none focus:border-white/30" />
            )}
            <ConfirmAction title={`Publish ${pending} change(s) to every server?`} confirmLabel="Publish to all servers" onConfirm={publish}
                description="This hub enforces the new map at once; every other server picks it up within about 30 seconds."
                disabled={!pending || busy} testid="field-map-publish"
                className="px-3 py-1.5 rounded-md text-xs font-medium bg-white text-black hover:bg-white/90 disabled:bg-white/5 disabled:text-white/30 inline-flex items-center gap-1.5 transition-colors">
                <Send className="w-3 h-3" /> {busy ? "Publishing…" : "Publish to all servers"}
            </ConfirmAction>
        </div>
    );
}
